import { createContext, createElement, useCallback, useContext, useEffect, useRef } from "react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { classifyDataThrough } from "./feedLag.mjs";
import { getSupabase, isSupabaseConfigured } from "./supabase";
import { scopeTenantTable } from "./tenantScope.mjs";

/**
 * MER-693: how far each event-grain source actually goes, fetched once per
 * (table, timeline column) per page and handed to every widget synchronously.
 *
 * WHY A PROVIDER AND NOT A QUERY PER TILE. Painting one tab already costs tens
 * of requests and a full tab tour hundreds, with no caching layer anywhere.
 * The answer this module supplies is a property of the SOURCE, not of the
 * widget, so every tile reading service_event.event_date shares one request
 * between them. That is the flagContext.ts / execKpiData.ts pattern: one probe
 * per tenant mount, cached, with an abort timeout so a degraded backend can
 * never hang the page.
 *
 * THE READ. `select <column> where tenant_id = ... and <column> is not null
 * order by <column> desc limit 1`. One row, one column, tenant-bound through
 * scopeTenantTable (MER-245) like every other customer-data query. It is the
 * same probe shape executeRowsQuery already runs for the MER-470 fallback, so
 * nothing new is being asked of the database.
 *
 * SOURCE-LEVEL, NOT SLICE-LEVEL, ON PURPOSE. No widget `filter` and no global
 * division / LOB selection is applied, because the whole saving is that one
 * read serves every tile on the source. So the answer is the newest row the
 * TENANT holds on that column, which can be a day or two newer than the newest
 * row behind a filtered tile. feedLag.mjs words the note accordingly ("Feed
 * through <Mon D>", a statement about the export rather than about the tile);
 * anyone tempted to reword it as a per-tile claim should read that note first.
 *
 * FAILURE IS SILENT ON SCREEN AND LOUD IN THE CONSOLE. A timeout, an RLS
 * refusal, an empty source or a column that turns out not to be a date all
 * resolve to null, and a null data-through leaves every tile behaving exactly
 * as it does today. This module can only ever ADD a note and withhold a
 * delta; it must never be able to take a number off the screen, so every
 * unknown answer has to fall back to the existing behaviour rather than to a
 * gate. The classification itself lives in feedLag.mjs (node:test drives it
 * with no Supabase), so no database text can reach a client.
 */

const PROBE_TIMEOUT_MS = 8000;

/** One cache entry per source column. The tenant scopes the whole provider. */
export function feedLagKey(table: string, column: string): string {
  return `${table}.${column}`;
}

/**
 * The newest value of `column` on `table` for this tenant, as an ISO date, or
 * null when it cannot be known. Never throws.
 */
export async function probeDataThrough(
  tenantId: string,
  table: string,
  column: string,
): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  try {
    const query = scopeTenantTable(
      getSupabase().from(table).select(column),
      tenantId,
      table,
    ) as unknown as {
      not: (c: string, op: string, v: unknown) => typeof query;
      order: (c: string, o: { ascending: boolean }) => typeof query;
      limit: (n: number) => typeof query;
      abortSignal: (s: AbortSignal) => PromiseLike<{
        data: unknown[] | null;
        error: { message?: string } | null;
      }>;
    };
    const result = await query
      .not(column, "is", null)
      .order(column, { ascending: false })
      .limit(1)
      .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS));
    const { dataThrough, problem } = classifyDataThrough(result, column);
    if (dataThrough === null) {
      console.warn(`data-through unavailable for ${feedLagKey(table, column)}: ${problem}`);
    }
    return dataThrough;
  } catch (err) {
    console.warn(
      `data-through unavailable for ${feedLagKey(table, column)}: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
    return null;
  }
}

export interface FeedLagContextValue {
  /**
   * The source's newest date, `null` when the probe failed or the source is
   * empty, and `undefined` while it has not answered yet. All three mean
   * "change nothing" to resolveFeedLag; they are kept distinct so a console
   * reader can tell a pending probe from a failed one.
   */
  lookup: (table: string | undefined, column: string | undefined) => string | null | undefined;
  /** Ask for a source's data-through. Idempotent: at most one read per key. */
  request: (table: string | undefined, column: string | undefined) => void;
}

const EMPTY: FeedLagContextValue = {
  lookup: () => undefined,
  request: () => {},
};

const FeedLagContext = createContext<FeedLagContextValue>(EMPTY);

/**
 * Probes lazily, once per (table, column), and hands every consumer the
 * cached answer. Mounted next to FlagMapProvider in CustomerDashboard so the
 * whole page shares one request per source.
 *
 * Lazy rather than eager because the set of sources on a page is not known
 * until its widgets render, and a fixed list here would drift from the
 * manifests the moment a widget is re-pointed.
 */
export function FeedLagProvider(props: { tenantId: string | null; children: ReactNode }) {
  const { tenantId, children } = props;
  const [known, setKnown] = useState<ReadonlyMap<string, string | null>>(new Map());
  // Keys already asked for. A ref, not state: starting a probe must not
  // re-render, or every widget would paint twice per source.
  const asked = useRef<Set<string>>(new Set());
  // The tenant those keys belong to. Seeded from the FIRST render's tenantId
  // rather than left empty, because React runs child effects BEFORE parent
  // ones: a reset that ran unconditionally on mount would erase the keys the
  // tiles had just registered, microseconds after their probes were launched,
  // and the next tab switch would fire a second identical read for every
  // source. Seeded this way the mount pass is a no-op and only a real tenant
  // change clears the cache.
  const probedTenant = useRef<string | null>(tenantId);

  useEffect(() => {
    // A tenant switch invalidates every answer: these are per-tenant facts.
    if (probedTenant.current === tenantId) return;
    probedTenant.current = tenantId;
    asked.current = new Set();
    setKnown(new Map());
  }, [tenantId]);

  const request = useCallback(
    (table: string | undefined, column: string | undefined) => {
      if (!tenantId || !table || !column) return;
      const key = feedLagKey(table, column);
      if (asked.current.has(key)) return;
      asked.current.add(key);
      void probeDataThrough(tenantId, table, column).then((dataThrough) => {
        setKnown((prev) => {
          const next = new Map(prev);
          next.set(key, dataThrough);
          return next;
        });
      });
    },
    [tenantId],
  );

  const value = useMemo<FeedLagContextValue>(
    () => ({
      lookup: (table, column) =>
        table && column ? known.get(feedLagKey(table, column)) : undefined,
      request,
    }),
    [known, request],
  );

  return createElement(FeedLagContext.Provider, { value }, children);
}

export function useFeedLag(): FeedLagContextValue {
  return useContext(FeedLagContext);
}
